/**
 * Native DTO types for the worksheet form response and its full dependency chain.
 *
 * These types were previously imported from `@packages/models`; they are now defined natively
 * so that `@packages/clients` has no dependency on `@packages/models`.
 */

// ============================================================
// Enums
// ============================================================

/**
 * Processing state of an asset as it moves through the upload pipeline.
 */
export enum AssetProcessingStateId {
    /** The asset is currently being uploaded to storage. */
    COMPLETE = 'COMPLETE',

    /** The asset encountered an error during processing. */
    ERROR = 'ERROR',

    /** The asset is being processed (e.g. thumbnail generation). */
    PROCESSING = 'PROCESSING',

    /** The asset upload is in progress. */
    UPLOADING = 'UPLOADING',
}

/**
 * Action to perform on a form element during a worksheet update.
 */
export enum FormElementAction {
    /** Delete the element from the worksheet. */
    Delete = 'DELETE',

    /** Insert a new element into the worksheet. */
    Insert = 'INSERT',

    /** Replace an existing element entirely. */
    Replace = 'REPLACE',

    /** Update an existing element in place. */
    Update = 'UPDATE',
}

/**
 * Discriminator for the type of input a form element represents.
 */
export enum FormElementTypeId {
    /** A checkbox input allowing multiple selections. */
    Checkbox = 'CHECKBOX',

    /** A date-only picker. */
    Date = 'DATE',

    /** A combined date and time picker. */
    DateTime = 'DATE_TIME',

    /** A dropdown/select input. */
    Dropdown = 'DROPDOWN',

    /** A dynamic grid element whose rows can be added at runtime. */
    DynamicGrid = 'DYNAMIC_GRID',

    /** A file upload input. */
    File = 'FILE',

    /** A free-text input (single line). */
    InputText = 'INPUT_TEXT',

    /** A free-text input (multi-line textarea). */
    InputTextarea = 'INPUT_TEXTAREA',

    /** A pressure test grid element. */
    PressureTestGrid = 'PRESSURE_TEST_GRID',

    /** A radio button input allowing a single selection. */
    Radio = 'RADIO',

    /** A numeric rating input. */
    Rating = 'RATING',

    /** A numeric rating input with an accompanying text field. */
    RatingWithText = 'RATING_WITH_TEXT',

    /** A non-interactive header text block. */
    TextHeader = 'TEXT_HEADER',

    /** A non-interactive paragraph text block. */
    TextParagraph = 'TEXT_PARAGRAPH',

    /** A time-only picker. */
    Time = 'TIME',

    /** A tire wear measurement grid element. */
    TireWear = 'TIRE_WEAR',

    /** A valve clearance measurement grid element. */
    ValveClearanceGrid = 'VALVE_CLEARANCE_GRID',

    /** A vehicle noise diagnosis grid element. */
    VehicleNoiseGrid = 'VEHICLE_NOISE_GRID',
}

/**
 * The submission status of a worksheet answer set.
 */
export const enum WorksheetAnswerStatus {
    /** The answers are saved as a draft and not yet submitted. */
    Draft = 'DRAFT',

    /** The answers have been formally submitted. */
    Submitted = 'SUBMITTED',

    /** The answers have been uploaded but not yet submitted. */
    Uploaded = 'UPLOADED',
}

/**
 * Image size variant for a worksheet asset media entry.
 */
export enum WorksheetAssetImageType {
    /** The full-resolution original image. */
    ORIGINAL = 'ORIGINAL',

    /** A standard thumbnail image. */
    THUMBNAIL = 'THUMBNAIL',

    /** A smaller thumbnail image. */
    THUMBNAIL_SMALL = 'THUMBNAIL_SMALL',
}

/**
 * Discriminator for the broad category of a worksheet asset.
 */
export enum WorksheetAssetType {
    /** A non-image file (PDF, document, etc.). */
    FILE = 'FILE',

    /** An image file. */
    IMAGE = 'IMAGE',
}

/**
 * Lifecycle status of a worksheet group (the container that holds worksheet versions).
 */
export enum WorksheetGroupStatus {
    /** The worksheet group has been archived and is no longer active. */
    Archived = 'ARCHIVED',

    /** The worksheet group is awaiting review. */
    Pending = 'PENDING',

    /** The worksheet group is in a limited pilot release. */
    Pilot = 'PILOT',

    /** The worksheet group is fully published and available. */
    Published = 'PUBLISHED',
}

// ============================================================
// Leaf types (no dependencies on other local types)
// ============================================================

/**
 * A column within a grid row, representing a single header cell.
 */
export type Column = {
    /** The display label for this column header. */
    columnLabel: string;

    /** The display order of this column within its row. */
    columnOrder?: number;

    /** The default value pre-filled in this column's cells. */
    defaultValue?: string;

    /** Whether a value is required for this column. */
    isRequired?: boolean;

    /** Nested sub-columns within this column. */
    subColumns?: SubColumn[];

    /** The unique identifier of the worksheet element grid cell. */
    worksheetElementGridId?: string;
};

/**
 * A conditional form element that appears when a specific option is selected.
 */
export type ConditionalElement = DraggableFormElement & {
    /** The option ID that triggers this conditional element to appear. */
    targetOptionId: string;
};

/**
 * The request body for creating a new worksheet form definition.
 */
export type CreateWorksheetFormRequestBody = {
    /** The unique identifier of the case type this worksheet is associated with. */
    caseTypeId: string;

    /** An optional description of the worksheet. */
    description?: string;

    /** The display name shown to users. */
    displayname: string;

    /** The form elements that make up this worksheet (excluding file attachments). */
    elements: Omit<WorksheetFormElement, 'formElementFile'>[];

    /** The lifecycle status of the worksheet group. */
    groupStatus: string;

    /** The NorthStar CRM identifier for the associated case type. */
    northstarId: string;

    /** The version number of this worksheet. */
    version: number;

    /** The unique identifier of the worksheet case group. */
    worksheetCaseGroupId?: string;

    /** The display name of the worksheet group. */
    worksheetGroupName: string;

    /** The publication status of this worksheet version. */
    worksheetStatus: WorksheetGroupStatus;
};

/**
 * A form element extended with drag-and-drop metadata for the form builder.
 */
export type DraggableFormElement = FormElement & {
    /** A description of the element shown in the form builder toolbox. */
    description: string;

    /** The icon name used to represent this element in the form builder. */
    iconName: string;

    /** The display title of the element in the form builder toolbox. */
    title: string;
};

/**
 * An element that has been mapped to a worksheet version, tracking its active status.
 */
export type ExistingElements = {
    /** The unique identifier of the worksheet element. */
    elementId: string;

    /** Whether this element is currently active in the worksheet version. */
    isActive: boolean;

    /** The mapping identifier linking the element to the worksheet version. */
    mappingId: string;

    /** The HTTP status code associated with this element mapping. */
    statusCode: number;

    /** The unique identifier of the worksheet this element belongs to. */
    worksheetId: string;
};

/**
 * A selected option within a form answer, capturing the user's choice.
 */
export type FormAnswerSelectedOption = {
    /** The free-text answer provided for this option, if applicable. */
    answerText?: string;

    /** The unique identifier of the selected option. */
    optionId: string;

    /** The display label for the selected option. */
    optionLabel?: string;
};

/**
 * A form element within a worksheet or standalone form.
 *
 * Represents a single input field, display block, or grid with all its metadata and options.
 */
export type FormElement = {
    /** The action to perform on this element during an update (insert, update, replace, delete). */
    action?: FormElementAction;

    /** Assets attached to answers for this element. */
    answerAssets?: WorksheetAsset[];

    /** Assets attached to this element's definition. */
    assets?: WorksheetAsset[];

    /** A file attached to this element. */
    formElementFile?: FormElementFile;

    /** Whether this element has conditional display rules. */
    formElementHasRule: boolean;

    /** The unique identifier of the form element. */
    formElementId: string;

    /** The display label for this element. */
    formElementLabel: string;

    /** The display order of this element within the form. */
    formElementOrder?: number;

    /** Placeholder text shown when the element's input is empty. */
    formElementPlaceholder?: string;

    /** The tooltip text for this element. */
    formElementTooltip?: string;

    /** The description text for this element. */
    formElementDescription?: string;

    /** The input type of this element. */
    formElementTypeId: FormElementTypeId;

    /** The grid rows for this element, if it is a grid type. */
    grid?: Grid[];

    /** Conditional child elements that appear based on selected options. */
    optionConditionsElements?: ConditionalElement[];

    /** The selectable options for this element. */
    options: FormElementOption[];

    /** Whether this element requires a response. */
    required: boolean;

    /** The options selected by the user for this element. */
    selectedOptions?: FormAnswerSelectedOption[];

    /** Grid cell answers for this element. */
    worksheetGridCellAnswers?: FormElementGridCellAnswer[];
};

// ============================================================
// Asset types
// ============================================================

/**
 * File metadata attached to a form element, including optional overlay information.
 */
export type FormElementFile = {
    /** The unique identifier of the asset backing this file. */
    assetId?: string;

    /** A caption or description for the file. */
    caption?: string;

    /** The browser File object for a pending upload. */
    file?: File;

    /** The display name of the file. */
    name?: string;

    /** Overlay image data applied on top of the original file. */
    overlay?: {
        /** The browser File object for the overlay. */
        file?: File;

        /** The display name of the overlay file. */
        name?: string;

        /** The URL for retrieving the overlay image. */
        url?: string;
    };

    /** The processing state of the asset backing this file. */
    status?: AssetProcessingStateId;

    /** The URL for retrieving the file content. */
    url?: string;
};

// ============================================================
// Form element file type
// ============================================================

/**
 * A single grid cell answer within a form element.
 */
export type FormElementGridCellAnswer = {
    /** The value entered by the user for this grid cell. */
    value: string;

    /** The unique identifier of the worksheet element grid cell. */
    worksheetElementGridId: string;
};

// ============================================================
// Form element option types
// ============================================================

/**
 * A single selectable option within a form element (radio, checkbox, dropdown, etc.).
 */
export type FormElementOption = {
    /** The free-text answer provided for this option, if applicable. */
    answerText?: string;

    /** The assets attached to this option. */
    assets?: WorksheetAsset[];

    /** Whether this option is an "Other" free-text option. */
    isOther?: boolean;

    /** The unique identifier of the option. */
    optionId: string;

    /** A file attached to this option. */
    optionFile?: FormElementFile;

    /** The display label for the option. */
    optionLabel: string;

    /** Placeholder text shown when the option's input is empty. */
    optionPlaceholder: string;

    /** The value submitted when this option is selected. */
    optionValue: string;

    /** The target element IDs revealed when this option is selected (conditional logic). */
    trgtElementIdList?: string[];
};

/**
 * A single row within a worksheet grid element, containing columns and optional sub-rows.
 */
export type Grid = {
    /** The columns within this row. */
    columns: Column[];

    /** The display label for this row. */
    rowLabel: string;

    /** The display order of this row within the grid. */
    rowOrder: number;

    /** Nested sub-rows within this row. */
    subRows?: SubRow[];
};

// ============================================================
// Grid types
// ============================================================

/**
 * A media entry (image, video, or file) associated with a worksheet asset.
 *
 * Contains metadata about dimensions, size, content type, and the URL for retrieval.
 */
export type Media = {
    /** The content MIME type of the media (e.g. "image/png"). */
    contentType: string;

    /** The ISO timestamp when the media was created. */
    createdTimestamp: Date;

    /** A human-readable formatted creation timestamp. */
    createdTimestampDescription: string;

    /** The height of the media in pixels. */
    height: number;

    /** A human-readable description of the height. */
    heightDescription: string;

    /** The unique identifier of the asset file, if applicable. */
    assetFileId?: string;

    /** The unique identifier of the asset image. */
    assetImageId: string;

    /** The image type variant (original, thumbnail, etc.). */
    assetImageTypeId: WorksheetAssetImageType;

    /** A human-readable description of the image type. */
    assetImageTypeIdDescription: string;

    /** The unique identifier of the asset video, if applicable. */
    assetVideoId?: string;

    /** The video type identifier, if applicable. */
    assetVideoTypeId?: string;

    /** The size of the media in bytes. */
    sizeInBytes: number;

    /** A human-readable description of the file size. */
    sizeInBytesDescription: string;

    /** The URL for retrieving the media content. */
    url: string;

    /** The width of the media in pixels. */
    width: number;

    /** A human-readable description of the width. */
    widthDescription: string;
};

/**
 * A selected option within a worksheet answer, capturing the user's choice and any free-text input.
 */
export type SelectedOption = {
    /** The free-text answer provided for this option, if applicable. */
    answerText: string;

    /** The unique identifier of the selected option. */
    optionId: string;

    /** The display label for the selected option. */
    optionLabel: string;
};

/**
 * A sub-column within a grid column, representing a nested header cell.
 */
export type SubColumn = {
    /** The display order of this sub-column within its parent column. */
    columnOrder: number;

    /** The default value pre-filled in this sub-column's cells. */
    defaultValue?: string;

    /** Whether a value is required for this sub-column. */
    isRequired: boolean;

    /** The display label for this sub-column header. */
    subColumnLabel: string;

    /** The value entered in this sub-column cell. */
    value?: string;

    /** The unique identifier of the worksheet element grid cell. */
    worksheetElementGridId?: string;
};

/**
 * A sub-row within a grid row, representing an additional row nested under the parent.
 */
export type SubRow = {
    /** The columns within this sub-row. */
    columns: SubRowColumn[];

    /** The display order of this sub-row within its parent row. */
    rowOrder: number;

    /** The display label for this sub-row. */
    subRowLabel: string;
};

/**
 * A column within a sub-row; identical to Column but without nested sub-columns.
 */
export type SubRowColumn = Omit<Column, 'subColumns'> & {
    /** Sub-columns are not supported in sub-row columns (always empty). */
    subColumns?: [];
};

// ============================================================
// Form element types
// ============================================================

/**
 * A worksheet asset (image or file) with full metadata and media entries.
 *
 * This is the worksheet-specific asset type, distinct from the Techline case asset type.
 */
export type WorksheetAsset = {
    /** The unique identifier of the asset. */
    assetId: string;

    /** The disposition identifier describing how the asset is categorized. */
    assetDispositionId: string;

    /** A human-readable description of the asset disposition. */
    assetDispositionIdDescription: string;

    /** The current processing state of the asset. */
    assetProcessingStateId: AssetProcessingStateId;

    /** A human-readable description of the processing state. */
    assetProcessingStateIdDescription: string;

    /** The broad category of the asset (image or file). */
    assetTypeId: WorksheetAssetType;

    /** A human-readable description of the asset type. */
    assetTypeIdDescription: string;

    /** A caption or description for the asset. */
    caption: string;

    /** The ISO timestamp when the asset was created. */
    createdTimestamp: Date;

    /** A human-readable formatted creation timestamp. */
    createdTimestampDescription: string;

    /** Whether the asset was imported from the legacy Siebel system. */
    importedFromSiebel: boolean;

    /** The media entries (images at various sizes) for this asset. */
    media: Media[];

    /** The display name of the asset. */
    name: string;

    /** The percentage of the upload that has been completed. */
    percentUploaded: number;

    /** A human-readable description of the upload percentage. */
    percentUploadedDescription: string;
};

/**
 * A single element within a worksheet form definition.
 *
 * Contains all metadata, options, grid data, and answer state for one worksheet question.
 */
export type WorksheetFormElement = {
    /** The action to perform on this element during an update. */
    action?: FormElementAction;

    /** Assets attached to answers for this element. */
    answerAssets?: WorksheetAsset[];

    /** Assets attached to this element's definition. */
    assets?: WorksheetAsset[];

    /** A file attached to this element. */
    formElementFile?: FormElementFile;

    /** The grid rows for this element, if it is a grid type. */
    grid: Grid[];

    /** The selectable options for this element. */
    options: WorksheetFormElementOptions[];

    /** The options selected by the user for this element. */
    selectedOptions?: SelectedOption[];

    /** Whether this element allows multiple selections. */
    worksheetElementAllowMultiple: boolean;

    /** The default value flag for this element. */
    worksheetElementDefaultValue: boolean;

    /** The description text for this element. */
    worksheetElementDescription: FormElement['formElementDescription'];

    /** Whether this element has conditional display rules. */
    worksheetElementHasRule: boolean;

    /** The unique identifier of the worksheet element. */
    worksheetElementId: string;

    /** The display label for this element. */
    worksheetElementLabel: FormElement['formElementLabel'];

    /** The display order of this element within the worksheet. */
    worksheetElementOrder: number;

    /** Placeholder text shown when the element's input is empty. */
    worksheetElementPlaceholder?: FormElement['formElementPlaceholder'];

    /** Whether this element requires a response. */
    worksheetElementRequired: boolean;

    /** The tooltip text for this element. */
    worksheetElementTooltip: FormElement['formElementTooltip'];

    /** The input type of this element. */
    worksheetElementTypeId: FormElement['formElementTypeId'];

    /** Grid cell answers for this element. */
    worksheetGridCellAnswers?: WorksheetGridCellAnswer[];
};

/**
 * An extended form element option used in worksheet forms, with additional display ordering and target mappings.
 */
export type WorksheetFormElementOptions = FormElementOption & {
    /** The assets attached to this option (worksheet-specific). */
    assets?: WorksheetAsset[];

    /** The display order of this option within the element. */
    optionDisplayOrder: number;

    /** The target worksheet element IDs revealed when this option is selected. */
    trgtWorksheetElementIdList: string[];
};

// ============================================================
// Worksheet form element type
// ============================================================

/**
 * Response DTO for retrieving a single worksheet form definition.
 *
 * Extends the creation request body with server-assigned identifiers, timestamps,
 * audit accounts, and submission status.
 *
 * Endpoint: GET /admin/worksheet/{worksheetFormId}
 */
export type WorksheetFormGetResponse = CreateWorksheetFormRequestBody & {
    /** The name of the case group this worksheet belongs to. */
    caseGroupName?: string;

    /** The case type display name. */
    caseType: string;

    /** The account that created this worksheet. */
    createdAccount: WorksheetUsersAccount;

    /** The ISO timestamp when the worksheet was created. */
    createdTimestamp: string;

    /** A human-readable formatted creation timestamp. */
    createdTimestampForDisplay: string;

    /** Elements that have been mapped to this worksheet version. */
    existingElements: ExistingElements[];

    /** The account that last updated this worksheet. */
    lastUpdatedAccount: WorksheetUsersAccount;

    /** The ISO timestamp of the last update. */
    lastUpdatedTimestamp: string;

    /** A human-readable formatted last-update timestamp. */
    lastUpdatedTimestampForDisplay: string;

    /** The unique identifier of the worksheet group. */
    worksheetGroupId: string;

    /** The unique identifier of this worksheet version. */
    worksheetId: string;

    /** The submission status of worksheet answers, if applicable. */
    worksheetSubmissionStatus?: WorksheetAnswerStatus;
};

// ============================================================
// Worksheet form request and response types
// ============================================================

/**
 * A single grid cell answer within a worksheet element.
 */
export type WorksheetGridCellAnswer = {
    /** The value entered by the user for this grid cell. */
    value: string;

    /** The unique identifier of the worksheet element grid cell. */
    worksheetElementGridId: string;
};

/**
 * Account information for a user associated with worksheet creation or updates.
 */
export type WorksheetUsersAccount = {
    /** The unique identifier of the user account. */
    accountId: string;

    /** The status of the user account. */
    accountStatusId: WorksheetGroupStatus;

    /** The user's first name. */
    firstName: string;

    /** The user's last name. */
    lastName: string;

    /** The user's locale setting (e.g. "en-US"). */
    locale: string;

    /** The user's full display name. */
    name: string;

    /** The user's timezone identifier. */
    timeZone: string;
};
